import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Layout, Grid as AntGrid, theme, Tooltip } from "antd";
import { useLocation } from "react-router-dom";
import { resetFields } from '@/store/reducers/headerContent';
// project import
import HeaderContent from "./HeaderContent";
import AppBarStyled from "./AppBarStyled";
import LocationSelector from "./LocationSelector";
import AskCpvButton from "@/pages/quality/cpv/assistant/AskCpvButton";
import { useSelector, useDispatch } from "react-redux";
import Breadcrumbs from "@/components/@extended/Breadcrumbs";
import { PageReturn } from "@/components/PageChrome";
import MenuItems from "@/menu-items/index";
import { useTheme } from '@/App';
import { useMbmrAlias } from '@/hooks/useAppSetting';

const { Header: AntHeader } = Layout;
const { useBreakpoint } = AntGrid;

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = ({ open, handleDrawerToggle }) => {
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const matchDownMD = !screens.lg;
  const navigation = MenuItems();
  const dispatch = useDispatch();
  const location = useLocation();
  const browserPath = location?.pathname;
  const selectedModule = useSelector((state) => state.moduleView);
  const moduleChange = selectedModule === "prod" ? "pack" : "prod";
  const finalPrModule = selectedModule === "prod" ? "MBMR" : "MBPR";
  const headerContent = useSelector((state) => state.headerContent);
  const allowedPaths = ["/recipeMetadata", "/recipe-view", "/edit-process"];

  // 2026-09-28 · the plant, named on the header.
  //
  // Every request already carries it — `getActivePlantId()` reads this same key for the
  // plant_id it sends — but nothing on screen said WHICH plant the page was about. On a
  // one-plant estate that reads as a missing fact; on a two-plant one it is the difference
  // between looking at your own areas and somebody else's. CPV-MT-01 F1: for a person who may
  // read more than one plant the name becomes the location selector (LocationSelector); this
  // stored name is only the fallback until the organisation tree is read.
  const activePlant = useMemo(() => {
    try {
      const raw = localStorage.getItem("active-plant");
      if (!raw || raw === "null" || raw === "undefined") return null;
      const p = JSON.parse(raw);
      return p?.plant_name || p?.name || p?.plant_code || null;
    } catch {
      return null;
    }
  }, []);

  const HEADER_LABEL_MAX_CHARS = 25;
  const truncateLabel = (text) =>
    typeof text === "string" && text.length > HEADER_LABEL_MAX_CHARS
      ? `${text.slice(0, HEADER_LABEL_MAX_CHARS)}...`
      : text;

  const HeaderInfo = ({ data = headerContent }) => {
    if (!headerContent || Object?.keys(headerContent)?.length === 0) {
      return (
        <Breadcrumbs
          navigation={navigation}
          title
          titleBottom
          card={false}
          divider={false}
        />
      );
    } else {
      if (
        (allowedPaths?.includes(browserPath) && !headerContent?.Process) ||
        browserPath?.startsWith("/process-config") ||
        // The record designer hosts the same process tree as /process-config and sets the
        // same two title fields. Without this line the else-branch below reset them on
        // every render, so the tree's header read "-" while the store had just been written.
        browserPath?.startsWith("/record-designer")
      ) {
        return (
          <div className="flex items-center gap-12 text-sm">
            {Object?.entries(data)?.map(([key, value], index) => (
              <div
                key={key}
                className={`${index !== 0 ? "border-l pl-4" : ""}`}
              >
                <span className="text-gray-500">{key === 'MBMR' ? mbmrAlias : key}</span>
                <div className="font-semibold">
                  {typeof value === "object" ? (
                    <>
                      <Tooltip title={value.text}>
                        <span>{truncateLabel(value.text)}</span>
                      </Tooltip>
                      {value.status && (
                        <span
                          className={`ml-2 text-xs px-2 py-0.5 rounded-md border
                      ${
                        value.status === "Running"
                          ? "bg-orange-100 text-orange-600 border-orange-300"
                          : value.status === "Completed"
                          ? "bg-green-100 text-green-600 border-green-300"
                          : "bg-gray-100 text-gray-600 border-gray-300"
                      }`}
                        >
                          {value.status}
                        </span>
                      )}
                    </>
                  ) : (
                    <Tooltip title={value}>
                      <span className="font-bold">{truncateLabel(value)}</span>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      } else {
        dispatch(resetFields());
        return (
          <Breadcrumbs
            navigation={navigation}
            title
            titleBottom
            card={false}
            divider={false}
          />
        );
      }
    }
  };

  // common header
  const mainHeader = (
    <div className={`px-6 py-0 h-16 flex items-center ${isDark ? 'bg-gray-800' : 'bg-white'}`} style={{ lineHeight: 1.5 }}>
      <div className="flex justify-between items-center w-full">
        <div className="flex-1">
          <div className={`w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-6">
              {/* Back (every page but home), then the breadcrumb or Batch / Process / Operation / Batch Size */}
              <PageReturn navigation={navigation} />
              <HeaderInfo />
            </div>
          </div>
        </div>

        {/* <div>
          <div className="flex items-center justify-center gap-2">
            <div>
              <img
                alt="Company Logo"
                src="https://png.pngtree.com/png-vector/20190304/ourmid/pngtree-growth-business-company-logo-png-image_728232.jpg"
                style={{ width: 42, height: 42, borderRadius: "50%" }}
              />
            </div>
            <div>
              <div className="text-lg font-semibold">
                Nexgensis
              </div>
              <div className="text-xs text-gray-500">
                Pune, Maharashtra
              </div>
            </div>
          </div>
        </div> */}

        <div>
          <div className="flex gap-2 items-center">
            <div className="flex gap-2 items-center">
              {/* CPV-AI-01 · "Ask CPV": the assistant, opened with this page's context */}
              <AskCpvButton />
              {/* CPV-MT-01 F1 · the location: Site ▸ Plant (several), or the one plant's name */}
              <LocationSelector fallbackLabel={activePlant} isDark={isDark} />
            </div>
            <HeaderContent />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!matchDownMD ? (
        <AppBarStyled
          open={open}
          style={{
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AntHeader
          className="fixed z-[1200]"
          style={{
            padding: 0,
            height: 64,
            background: isDark ? token.colorBgContainer : '#fff',
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AntHeader>
      )}
    </>
  );
};

Header.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

export default Header;
